<script>
/* global axios */
import { useAlert } from 'dashboard/composables';
import LoadingState from 'dashboard/components/widgets/LoadingState.vue';
import ErrorState from 'dashboard/components/widgets/ErrorState.vue';
import EmptyState from 'dashboard/components/widgets/EmptyState.vue';
import StaffCard from './StaffCard.vue';
import AssignStaffScheduleModal from './AssignStaffScheduleModal.vue';

export default {
  name: 'ScheduleStaff',

  props: {
    staffList: {
      type: Array,
      default: () => [],
    },
    isLoading: {
      type: Boolean,
      required: true,
    },
    scheduleId: {
      type: String,
      required: true,
    },
  },

  components: {
    LoadingState,
    ErrorState,
    EmptyState,
    StaffCard,
    AssignStaffScheduleModal,
  },

  computed: {
    isOnboardingLinkStaffToSchedule() {
      return this.$route.query.onboarding === 'true' && this.$route.query.step === 'link_staff_to_schedule';
    },
    apiUrl() {
      return `/api/v1/accounts/${this.accountId}/proxy/knowledge`;
    },
    accountId() {
      return this.$route.params.accountId;
    },
  },

  data() {
    return {
      showCreateModal: false,
      selectedStaff: null,
      isError: false,
    };
  },

  methods: {
    async handleOpenDelete(id) {
      await this.unassignStaff(id);
      this.handleRefresh();
    },

    async unassignStaff(staffId) {
      try {
        await axios.delete(
          `${this.apiUrl}/accounts/${this.accountId}/staff/${staffId}/schedules/${this.scheduleId}`
        );
        useAlert('Staff unassigned');
        this.handleRefresh();
      } catch(err) {
        useAlert('Failed to unassign staff');
        console.error(err);
      }
    },

    handleRefresh() {
      this.$emit('refresh');
    },
  },
};
</script>

<template>
  <div class="p-4 bg-white dark:bg-slate-700 rounded-md border-2 border-slate-100 dark:border-slate-50">
    <!-- Header -->
    <div class="flex justify-between items-center mb-4">
      <h1 class="text-xl font-semibold text-gray-900 dark:text-gray-100">
        Staffs
      </h1>
      <div class="relative inline-block">
        <woot-button
          variant="smooth"
          color-scheme="primary"
          icon="plus-sign"
          @click="showCreateModal = true"
        >
          Assign Staff
        </woot-button>
        <!-- Onboarding Hint Tooltip -->
        <div v-if="isOnboardingLinkStaffToSchedule && (!staffList || staffList.length === 0)" class="absolute -bottom-10 right-0 transform translate-x-1 whitespace-nowrap z-10 pointer-events-none">
          <div class="bg-slate-900 dark:bg-slate-100 text-white dark:text-slate-900 text-xs font-medium px-3 py-2 rounded-md shadow-lg animate-bounce-subtle flex gap-1 items-center">
            Hint: Click to assign staff to schedule <fluent-icon icon="caret-up" />
          </div>
        </div>
      </div>
    </div>

    <!-- States -->
    <LoadingState v-if="isLoading" message="Loading staffs..." />

    <ErrorState v-else-if="!isLoading && isError" />

    <EmptyState
      v-else-if="!isLoading && !isError && staffList.length === 0"
      title="No staffs Found"
      message="Assign a new staff to get started"
    />

    <div v-else>
      <StaffCard 
        v-for="item in staffList"
        :key="item.id"
        :staff="item"
        @delete="handleOpenDelete(item.id)"
      />
    </div>

    <!-- Assign Staff Modal -->
    <AssignStaffScheduleModal
      v-if="showCreateModal"
      :staffs="staffList"
      :scheduleId="scheduleId"
      @close="showCreateModal = false"
      @refresh="handleRefresh"
    />
  </div>
</template>